import { ScrollStrategyOptions } from '@angular/cdk/overlay';
import { DOCUMENT } from '@angular/common';
import {
    HTTP_INTERCEPTORS,
    HttpClientModule,
} from '@angular/common/http';
import {
    ErrorHandler,
    NgModule,
} from '@angular/core';
import {
    MatMomentDateModule,
    MomentDateModule,
} from '@angular/material-moment-adapter';
import {
    ErrorStateMatcher,
    MAT_LABEL_GLOBAL_OPTIONS,
    MAT_RIPPLE_GLOBAL_OPTIONS,
} from '@angular/material/core';
import { MatDatepickerIntl } from '@angular/material/datepicker';
import { MatDialogModule } from '@angular/material/dialog';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { MAT_PROGRESS_SPINNER_DEFAULT_OPTIONS } from '@angular/material/progress-spinner';
import { MAT_TABS_CONFIG } from '@angular/material/tabs';
import { MAT_TOOLTIP_DEFAULT_OPTIONS } from '@angular/material/tooltip';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { RouterModule } from '@angular/router';
import { ServiceWorkerModule } from '@angular/service-worker';
import {
    AppStateFacade,
    Setting,
} from '@app-state';
import {
    AppRippleOptions,
    MainComponent,
} from '@app/main.component';
import {
    UIPATH_BOOTSTRAP_LISTENER,
    UIPATH_INITIALIZER,
} from '@app/uipath.initializer';
import { TRANSLATE_MODULE_CONFIG } from '@app/uipath.translations';
import { UiPersistentNotificationModule } from '@components';
import { COLOR_PROVIDER } from '@components/ui-chart/chart-color-provider';
import { DATA_MATRIX_BACKGROUND_COLOR_RESOLVER } from '@components/ui-data-matrix/ui-data-matrix.component';
import { UiDialogComponent } from '@components/ui-dialog/ui-dialog.component';
import { UiDialogModule } from '@components/ui-dialog/ui-dialog.module';
import {
    UiAutocompleteIntl,
    UiMatDatepickerIntl,
    UiMatPaginatorIntl,
    UiSnackIntl,
    UiTableIntl,
} from '@intl';
import { BaseModule } from '@modules';
import {
    TranslateModule,
    TranslateService,
} from '@ngx-translate/core';
import {
    ColorService,
    Logger,
    LoggerFactory,
} from '@services';
import { ThemeColorProvider } from '@services/color/color.provider';
import { HttpEmptyInterceptor } from '@services/generics/http.empty.intercept';
import {
    Interceptor,
    INTERCEPTOR_DEPENDENCIES,
} from '@services/generics/http.intercept';
import { HttpMaintenanceInterceptor } from '@services/generics/http.maintenance.intercept';
import { UiGridIntl } from '@uipath/angular/components/ui-grid';
import { UiSnackbarIntl } from '@uipath/angular/components/ui-snackbar';
import { UiSuggestIntl } from '@uipath/angular/components/ui-suggest';
import { UI_DATEFORMAT_OPTIONS } from '@uipath/angular/directives/ui-dateformat';
import { UI_SECONDFORMAT_OPTIONS } from '@uipath/angular/directives/ui-secondformat';
import {
    ERROR_HANDLER_DEPENDENCIES,
    GlobalErrorHandler,
} from '@utils/errorHandler/globalErrorHandler';
import {
    SENTRY_DSN,
    SENTRY_TRACKED_DOMAINS,
} from '@utils/errorHandler/sentryErrorHandler';
import { UiScrollStrategyOptions } from '@utils/scroll-stategy-options';

import { environment } from '../environments/environment';
import { UiPathRoutingModule } from './uipath-routing.module';
import { UiPathModule } from './uipath.module';
import { UiErrorStateMatcher } from './validators/error-state-matcher';

const QA_DEV_HOSTNAMES = [
    /qa\.test$/,
    /^devtest-e2e-.*azurewebsites\.net$/,
];

export const GLOBAL_INTERCEPTORS = [{
    provide: HTTP_INTERCEPTORS,
    useClass: HttpEmptyInterceptor,
    multi: true,
}, {
    provide: HTTP_INTERCEPTORS,
    useClass: HttpMaintenanceInterceptor,
    multi: true,
}, {
    provide: HTTP_INTERCEPTORS,
    useClass: Interceptor,
    deps: INTERCEPTOR_DEPENDENCIES,
    multi: true,
}];

const PROVIDERS = [...GLOBAL_INTERCEPTORS, {
    provide: ErrorHandler,
    useClass: GlobalErrorHandler,
    deps: ERROR_HANDLER_DEPENDENCIES,
}, {
    provide: SENTRY_TRACKED_DOMAINS,
    useValue: environment.production ?
        [
            /platform\.uipath\.com$/,
            /deskover\.local$/,
            ...QA_DEV_HOSTNAMES,
        ] :
        [],
}, {
    provide: SENTRY_DSN,
    useFactory: (_document: Document) => {
        if (!environment.production) { return 'https://6c326249aa8842179f59905f237436ea@sentry.io/1372845'; }

        if (QA_DEV_HOSTNAMES.some(reg => reg.test(_document.location.hostname))) {
            return 'https://6529911c060f4ab08a17355bbe062282@sentry.io/1387287';
        }

        return 'https://93064310bfe14da7b6446725f524283c@sentry.io/1372840';
    },
    deps: [DOCUMENT],
}, {
    provide: MAT_RIPPLE_GLOBAL_OPTIONS,
    useExisting: AppRippleOptions,
}, {
    provide: MAT_LABEL_GLOBAL_OPTIONS,
    useValue: {
        float: 'always',
    },
}, {
    provide: MAT_TABS_CONFIG,
    useValue: {
        animationDuration: '0ms',
    },
}, {
    provide: MAT_TOOLTIP_DEFAULT_OPTIONS,
    useValue: {
        showDelay: 300,
    },
}, {
    provide: Logger,
    useClass: LoggerFactory({
        production: environment.production,
    }),
    multi: false,
}, {
    provide: ErrorStateMatcher,
    useClass: UiErrorStateMatcher,
}, {
    provide: MatDatepickerIntl,
    useClass: UiMatDatepickerIntl,
    deps: [TranslateService, AppStateFacade],
}, {
    provide: UiSuggestIntl,
    useClass: UiAutocompleteIntl,
    deps: [TranslateService],
}, {
    provide: UiSnackbarIntl,
    useClass: UiSnackIntl,
    deps: [TranslateService],
}, {
    provide: UiGridIntl,
    useClass: UiTableIntl,
    deps: [TranslateService, AppStateFacade],
}, {
    provide: MatPaginatorIntl,
    useClass: UiMatPaginatorIntl,
    deps: [TranslateService, AppStateFacade],
}, {
    provide: COLOR_PROVIDER,
    useClass: ThemeColorProvider,
}, {
    provide: DATA_MATRIX_BACKGROUND_COLOR_RESOLVER,
    useFactory: (color: ColorService) => () => color.theme.material.background.card,
    deps: [ColorService],
}, {
    provide: UI_DATEFORMAT_OPTIONS,
    useFactory: (appState: AppStateFacade) => ({
        timezone: () => appState.settings.get(Setting.timezone) || 'Etc/UTC',
        redraw$: appState.language.change$,
    }),
    deps: [AppStateFacade],
}, {
    provide: UI_SECONDFORMAT_OPTIONS,
    useFactory: (appState: AppStateFacade) => ({
        redraw$: appState.language.change$,
    }),
    deps: [AppStateFacade],
}, {
    provide: MAT_PROGRESS_SPINNER_DEFAULT_OPTIONS,
    useValue: {
        strokeWidth: 5,
    },
}, {
    provide: ScrollStrategyOptions,
    useClass: UiScrollStrategyOptions,
}];

@NgModule({
    declarations: [
        MainComponent,
    ],
    imports: [
        BrowserModule,
        HttpClientModule,
        BrowserAnimationsModule,
        MatDialogModule,
        RouterModule,
        UiDialogModule,
        MatMomentDateModule,
        MomentDateModule,
        UiPathRoutingModule,
        UiPersistentNotificationModule,
        BaseModule.forRoot(),
        UiPathModule.forRoot(),
        TranslateModule.forRoot(TRANSLATE_MODULE_CONFIG),
        ServiceWorkerModule.register('ngsw-worker.js', {
            enabled: environment.production,
        }),
    ],
    providers: [
        UIPATH_INITIALIZER,
        UIPATH_BOOTSTRAP_LISTENER,
        ...PROVIDERS,
    ],
    entryComponents: [
        UiDialogComponent,
    ],
    bootstrap: [MainComponent],
})
export class AppModule {
    constructor(logger: Logger) {
        const workerScript = 'ngsw-worker.js';

        const isServiceWorkerEnabled = 'serviceWorker' in navigator && environment.production;

        if (!isServiceWorkerEnabled) { return; }

        navigator.serviceWorker
            .getRegistration(workerScript)
            .then(worker => {
                if (!!worker) { return; }

                navigator.serviceWorker
                    .register(workerScript)
                    .then(() => logger.log('Service Worker Successfully registered'))
                    .catch(() => logger.warn('Service Worker did not register'));
            })
            .catch(e => logger.error(e));
    }
}
